/** @jsxRuntime classic */
/** @jsx jsx */
/** @jsxFrag Fragment */
import type { BoxProps } from '@mantine/core';
import { Box, Button, Image } from '@mantine/core';
import {
  IconFocus2,
  IconZoomIn,
  IconZoomOut,
  IconZoomPan,
} from '@tabler/icons-react';
import React, { useEffect } from 'react';
import {
  MiniMap,
  TransformComponent,
  TransformWrapper,
  useControls,
} from 'react-zoom-pan-pinch';
import { jsx } from 'theme-ui';
import getMantineCloudinaryImage from './getMantineCloudinaryImage';
import { ScaleProvider } from './_FloorPlan.useScale';

export default function FloorPlanMain({
  title,
  id,
  imagePublicId,
  imageUrl,
  width,
  height,
  elementIdToZoom,
  primaryColor,
  contrastColor: _,
  children,
  shouldAutoZoom,
  ...rest
}: BoxProps & {
  children?: React.ReactNode;
  title: string;
  id: string;
  imagePublicId: string;
  imageUrl?: string;
  width: number;
  height: number;
  primaryColor: string | undefined;
  contrastColor: string | undefined;
  elementIdToZoom?: string | HTMLElement;
  shouldAutoZoom?: boolean;
}) {
  if (!imagePublicId && !imageUrl) return null;
  return (
    <Box
      // Reset the plan state if we're switching between plans.
      key={id}
      maw="100%"
      sx={{
        border: `3px solid ${primaryColor || 'transparent'}`,
        overflow: 'hidden',
        aspectRatio: `${width} / ${height}`,
      }}
      {...rest}
    >
      <TransformWrapper minScale={0.5}>
        <ZoomControls
          pos="absolute"
          left={8}
          top={8}
          style={{ zIndex: 5 }}
          elementIdToZoom={elementIdToZoom}
          shouldAutoZoom={shouldAutoZoom}
        />
        <Box pos="absolute" right={8} top={8}>
          <MiniMap width={100} borderColor={primaryColor}>
            {imageUrl ? (
              <Image src={imageUrl} alt={title} maw="100%" />
            ) : (
              <Image
                {...getMantineCloudinaryImage({
                  layout: 'constrained',
                  src: imagePublicId,
                  width,
                  height,
                  maw: '100%',
                  loader: ({ src, width, height, quality }) =>
                    `https://res.cloudinary.com/bottlebooks/image/upload/w_${width},h_${height},c_pad,g_center,f_auto,q_${quality}/${src}`,
                })}
                alt={title}
              />
            )}
          </MiniMap>
        </Box>
        <TransformComponent
          wrapperStyle={{ maxWidth: '100%' }}
          contentStyle={{ width: '100%' }}
        >
          {/* <Box sx={{ position: 'relative', width: floorPlan.width }}> */}
          {imageUrl ? (
            <Image src={imageUrl} alt={title} />
          ) : (
            <Image
              {...getMantineCloudinaryImage({
                layout: 'constrained',
                src: imagePublicId,
                width,
                height,
                loader: ({ src, width, height, quality }) =>
                  `https://res.cloudinary.com/bottlebooks/image/upload/w_${width},h_${height},c_pad,g_center,f_auto,q_${quality}/${src}`,
              })}
              fit="contain"
              alt={title}
              flex="1 1 0%"
            />
          )}
          <ScaleProvider>{children}</ScaleProvider>
        </TransformComponent>
      </TransformWrapper>
    </Box>
  );
}

function ZoomControls({
  elementIdToZoom,
  shouldAutoZoom,
  ...rest
}: BoxProps & {
  elementIdToZoom?: string | HTMLElement;
  shouldAutoZoom?: boolean;
}) {
  const { zoomIn, zoomOut, resetTransform, zoomToElement } = useControls();
  useEffect(() => {
    if (!shouldAutoZoom) return;
    if (!elementIdToZoom) return;
    const timeout = setTimeout(() => {
      zoomToElement(elementIdToZoom, 2, 500);
    }, 100);
    return () => clearTimeout(timeout);
  }, [elementIdToZoom, shouldAutoZoom, zoomToElement]);

  return (
    <Box bg="white" {...rest}>
      <Button.Group>
        <Button px="sm" variant="default" onClick={() => zoomIn()}>
          <IconZoomIn size={20} />
        </Button>
        <Button px="sm" variant="default" onClick={() => resetTransform()}>
          <IconZoomPan size={20} />
        </Button>
        <Button px="sm" variant="default" onClick={() => zoomOut()}>
          <IconZoomOut size={20} />
        </Button>
        {elementIdToZoom ? (
          <Button
            px="sm"
            variant="default"
            onClick={() => zoomToElement(elementIdToZoom, 3)}
          >
            <IconFocus2 size={20} />
          </Button>
        ) : null}
      </Button.Group>
    </Box>
  );
}
